import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Lock } from 'lucide-react';
import type { User, Persona } from '@/types';
import { apiClient } from '@/services/api-client';

interface AssignPersonaDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AssignPersonaDialog({
  user,
  open,
  onOpenChange,
  onSuccess,
}: AssignPersonaDialogProps) {
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [currentPersonaId, setCurrentPersonaId] = useState<string | null>(null);
  const [currentPersonaName, setCurrentPersonaName] = useState<string | null>(null);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const alreadyAssigned = currentPersonaId !== null;

  useEffect(() => {
    if (open && user) {
      setError(null);
      setIsFetching(true);

      Promise.all([
        apiClient.get<Persona[]>('/api/v1/admin/personas/all'),
        apiClient.get<{ id: string; code: string; name: string } | null>(
          `/api/v1/admin/users/${user.id}/persona`
        ),
      ])
        .then(([personasRes, currentRes]) => {
          if (personasRes.success && personasRes.data) {
            setPersonas(personasRes.data);
          }
          const current = currentRes.success && currentRes.data ? currentRes.data : null;
          setCurrentPersonaId(current?.id ?? null);
          setCurrentPersonaName(current ? `${current.name} (${current.code})` : null);
          setSelectedPersonaId(current?.id ?? null);
        })
        .catch(() => {
          setError('Failed to load personas');
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  }, [open, user]);

  const handleSubmit = async () => {
    if (!user || alreadyAssigned || !selectedPersonaId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.put(`/api/v1/admin/users/${user.id}/persona`, {
        personaId: selectedPersonaId,
      });
      if (response.success) {
        onOpenChange(false);
        onSuccess?.();
      } else {
        setError(response.error?.message || 'Failed to assign persona');
      }
    } catch {
      setError('An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  const hasChanged = selectedPersonaId !== null && selectedPersonaId !== currentPersonaId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{alreadyAssigned ? 'Assigned Persona' : 'Assign Persona'}</DialogTitle>
          <DialogDescription>
            {alreadyAssigned
              ? `${user.firstName || user.email} has been assigned a persona. Once assigned, personas cannot be changed from this interface.`
              : `Assign a persona to ${user.firstName || user.email}. This determines which story cards they see. Once assigned, this cannot be changed.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isFetching ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : alreadyAssigned ? (
            <div className="space-y-2">
              <Label>Current Persona</Label>
              <div className="flex items-center gap-2 rounded-md border px-3 py-2 bg-muted/50">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{currentPersonaName}</span>
                <Badge variant="secondary" className="ml-auto">
                  Locked
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Persona assignments are permanent and cannot be modified from the dashboard.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="persona">Persona</Label>
              <Select
                value={selectedPersonaId ?? undefined}
                onValueChange={(value) => setSelectedPersonaId(value)}
              >
                <SelectTrigger id="persona">
                  <SelectValue placeholder="Select a persona" />
                </SelectTrigger>
                <SelectContent>
                  {personas.map((persona) => (
                    <SelectItem key={persona.id} value={persona.id}>
                      {persona.name} ({persona.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {alreadyAssigned ? 'Close' : 'Cancel'}
          </Button>
          {!alreadyAssigned && (
            <Button onClick={handleSubmit} disabled={isLoading || isFetching || !hasChanged}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign Persona
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
